import { type AimPoint, type GameData, type Participant, distance } from "./model.ts";
import { acquireShot, acquireSpark } from "./pool.ts";
import { randomBetween } from "./random.ts";

export function addSparks(game: GameData, x: number, y: number, color: string, count = 12) {
  for (let index = 0; index < count; index += 1) {
    const angle = randomBetween(game.random, 0, Math.PI * 2);
    const speed = randomBetween(game.random, 45, 175);
    acquireSpark(game, {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: randomBetween(game.random, 0.45, 0.9),
      color,
    });
  }
}

/**
 * Fires one orb for `shooter`. Works for humans and bots alike — a bot simply has
 * its aim produced by the AI instead of arriving over the wire.
 */
export function launchShot(
  game: GameData,
  shooter: Participant,
  aim: AimPoint,
  speed: number,
  fireInterval: number,
  origin?: { x: number; y: number },
  charge = 0,
  cooldown = 0,
) {
  if (!game.running || !shooter.alive || shooter.orbs < 1 || shooter.attackCooldown > 0) return false;

  let targetX = aim.x;
  let targetY = aim.y;
  if (!aim.active || distance(shooter.x, shooter.y, targetX, targetY) < 30) {
    // No usable aim: fall back to the nearest live opponent so a blind tap still does
    // something sensible.
    const target = game.participants
      .filter((participant) => participant.alive && participant.id !== shooter.id)
      .sort((a, b) => distance(shooter.x, shooter.y, a.x, a.y) - distance(shooter.x, shooter.y, b.x, b.y))[0];
    targetX = target?.x ?? shooter.x;
    targetY = target?.y ?? shooter.y - 100;
  }

  const shotOrigin = origin ?? shooter;
  const dx = targetX - shotOrigin.x;
  const dy = targetY - shotOrigin.y;
  const length = Math.max(1, Math.hypot(dx, dy));
  // Range is measured from the core, not from the sphere the orb happened to be
  // sitting on. The orb leaves its visible position — that is what a player sees —
  // but starting the odometer there made the reach depend on where the orbit had
  // rotated to, a twenty-pixel swing on the same setting, and made the number in
  // the panel mean something other than what it says.
  const aheadOfCore = ((shotOrigin.x - shooter.x) * dx + (shotOrigin.y - shooter.y) * dy) / length;
  const shot = acquireShot(game, {
    id: game.nextId++,
    owner: shooter.id,
    x: shotOrigin.x,
    y: shotOrigin.y,
    vx: (dx / length) * speed,
    vy: (dy / length) * speed,
    life: 0,
    traveled: aheadOfCore,
    returning: false,
    stolen: false,
    // The ring leaves with the orb. Spent on firing rather than on landing: an orb
    // you can see coming should be one you can dodge, and a charge you cannot waste
    // is a charge you never have to aim.
    surge: charge,
    taken: 0,
    color: shooter.color,
  });
  if (!shot) return false;
  shooter.orbs -= 1;
  // Firing a ring starts the wait. Without it the orbs a surge steals come straight
  // home, close the ring again and arm the next shot — a charge every second.
  if (charge > 0) shooter.surgeCooldown = cooldown;
  shooter.attackCooldown = fireInterval;
  return true;
}

export function advanceOutboundShot(shot: GameData["shots"][number], dt: number) {
  const dx = shot.vx * dt;
  const dy = shot.vy * dt;
  shot.x += dx;
  shot.y += dy;
  shot.traveled = (Number.isFinite(shot.traveled) ? shot.traveled : 0) + Math.hypot(dx, dy);
}

export function hasReachedShotRange(shot: GameData["shots"][number], range: number) {
  return shot.traveled >= range;
}

export const SHIELD_DURATION = 1.35;
export const SHIELD_COOLDOWN = 5;

export function activateShield(game: GameData, participant: Participant) {
  if (!game.running || !participant.alive || participant.shieldCooldown > 0 || participant.orbs < 1) return false;
  participant.boostRemaining = 0;
  participant.shield = SHIELD_DURATION;
  participant.shieldCooldown = SHIELD_COOLDOWN;
  return true;
}

export function activateBoost(game: GameData, participant: Participant, duration = 1) {
  if (!game.running || !participant.alive || participant.orbs < 1) return false;
  if (participant.shield > 0 || participant.boostRemaining > 0) return false;
  participant.orbs -= 1;
  participant.boostRemaining = duration;
  return true;
}
